import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface NavItem {
  path: string;
  label: string;
}

const MAIN_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Home' },
  { path: '/outlook', label: '7-Day Outlook' },
  { path: '/daily', label: 'Daily View' },
  { path: '/monthly', label: 'Monthly View' },
];

const SUPPORT_NAV: NavItem = { path: '/support', label: 'Support' };

function Header() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const isPlus = isAuthenticated && user?.tier === 'plus';
  const navigate = useNavigate();
  const location = useLocation();

  const contentRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // True when the full inline nav doesn't fit and links move into the menu.
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = isAuthenticated ? [...MAIN_NAV, SUPPORT_NAV] : [SUPPORT_NAV];
  navItems.splice(navItems.length - 1, 0, { path: '/blog', label: 'Blog' });
  const showMenuButton = isAuthenticated || collapsed;

  // Compare the width of a hidden, fully-expanded copy of the header against
  // the space available, so we collapse exactly when the links would overrun.
  useLayoutEffect(() => {
    const content = contentRef.current;
    const measure = measureRef.current;
    if (!content || !measure) return;

    const check = () => setCollapsed(measure.scrollWidth > content.clientWidth);
    check();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }
    const observer = new ResizeObserver(check);
    observer.observe(content);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [isAuthenticated, isPlus]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, isAuthenticated]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || toggleRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const go = async (path: string) => {
    setMenuOpen(false);
    if (path === '/blog' && Capacitor.isNativePlatform()) {
      try {
        await Browser.open({ url: 'https://standlogic.app/blog' });
      } catch (err) {
        console.error('Unable to open the StandLogic blog:', err);
        window.alert('Unable to open the blog. Please try again or visit https://standlogic.app/blog.');
      }
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path ||
    (path === '/blog' && location.pathname.startsWith('/blog/'));

  const renderInlineLinks = (interactive: boolean) => (
    <nav className="nav-links" aria-label={interactive ? 'Main navigation' : undefined}>
      {navItems.map((item) => (
        <button
          key={item.path}
          className={`nav-link ${isActive(item.path) ? 'nav-link-active' : ''}`}
          onClick={interactive ? () => go(item.path) : undefined}
          tabIndex={interactive ? undefined : -1}
          aria-current={interactive && isActive(item.path) ? 'page' : undefined}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );

  const logo = (interactive: boolean) => (
    <div
      className="logo"
      onClick={interactive ? () => navigate('/') : undefined}
      onKeyDown={interactive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/');
        }
      } : undefined}
      role={interactive ? 'link' : undefined}
      aria-label={interactive ? `StandLogic${isPlus ? ' Plus' : ''} home` : undefined}
      tabIndex={interactive ? 0 : -1}
    >
      <img src="/favicon.png?v=2" alt="" className="logo-img" />
      <h1>StandLogic</h1>
      {isPlus && <span className="tier-badge tier-plus">Plus</span>}
    </div>
  );

  const menuButtonInner = (
    <span className="menu-toggle-bars" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );

  return (
    <header className="header">
      <div className="header-content" ref={contentRef}>
        {/* Hidden full-width layout used only to detect overrun. */}
        <div className="header-measure-clip" aria-hidden="true">
          <div className="header-measure" ref={measureRef}>
            <div className="header-left">
              {logo(false)}
              {renderInlineLinks(false)}
            </div>
            {isAuthenticated && (
              <span className="menu-toggle">{menuButtonInner}</span>
            )}
          </div>
        </div>

        <div className="header-left">
          {logo(true)}
          {!collapsed && renderInlineLinks(true)}
        </div>

        {showMenuButton && (
          <div className="menu-container">
            <button
              ref={toggleRef}
              className={`menu-toggle ${menuOpen ? 'menu-toggle-open' : ''}`}
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="header-menu"
            >
              {menuButtonInner}
            </button>

            {menuOpen && (
              <div className="menu-panel" id="header-menu" ref={menuRef}>
                <nav aria-label="Menu">
                  {collapsed && (
                    <>
                      {navItems.map((item) => (
                        <button
                          key={item.path}
                          className={`menu-item ${isActive(item.path) ? 'menu-item-active' : ''}`}
                          onClick={() => go(item.path)}
                          aria-current={isActive(item.path) ? 'page' : undefined}
                        >
                          {item.label}
                        </button>
                      ))}
                      {isAuthenticated && <div className="menu-divider" role="separator" />}
                    </>
                  )}
                  {isAuthenticated && (
                    <>
                      <button
                        className={`menu-item ${isActive('/profile') ? 'menu-item-active' : ''}`}
                        onClick={() => go('/profile')}
                        aria-current={isActive('/profile') ? 'page' : undefined}
                      >
                        Profile
                      </button>
                      {isAdmin && !Capacitor.isNativePlatform() && (
                        <button
                          className={`menu-item ${isActive('/admin') ? 'menu-item-active' : ''}`}
                          onClick={() => go('/admin')}
                          aria-current={isActive('/admin') ? 'page' : undefined}
                        >
                          Admin
                        </button>
                      )}
                      <div className="menu-divider" role="separator" />
                      <button className="menu-item" onClick={handleLogout}>
                        Logout
                      </button>
                    </>
                  )}
                </nav>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
